import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useCallback, useEffect, useState } from 'react'
import { asText } from '@prismicio/helpers'
import { SliceZone } from '@prismicio/react'
import { IAdvertCard } from 'data-transfers/dto'
import { IPaginated } from 'data-transfers/responses'
import { format } from 'date-fns'
import { AdvertTranslation, AdvertVisibilityType } from 'enums'

import AdvertSwiper from 'components/AdvertSwiper'
import AlternativeArticleLang from 'components/AlternativeArticleLang'
import {
  ArticleComments,
  ArticleHead,
  RelatedArticles
} from 'components/Articles'
import { BoundedVariant } from 'components/Bounded'
import { IMetaTagsProps } from 'components/MetaTags/types'
import { CreateArticleCommentModal } from 'components/modals'
import { IMarkupSchemas } from 'components/SEOMarkupSchema'
import Title from 'components/Title'
import { components } from 'slices'

import { getAdvertsPublic } from 'hooks/useAdvert/advert.service'
import { useArticleComments } from 'hooks/useArticleComments'
import { useLanguageSettings } from 'context/language-manager'

import { CommunitypostDocument, CommunityQouteSlice } from 'types/prismic'
import { getLocalisedLink } from 'utils'
import adUtils from 'utils/adUtil'
import { getEnv } from 'utils/env/env.util'
import { getFullPageByUID, getPrismicLangCode } from 'utils/prismic'
import { createClient } from 'env/prismicio'

const SocialLinks = dynamic(() => import('components/SocialLinks'), {
  ssr: false
})

const { baseUrl } = getEnv()
const CommunityPage = ({
  adverts,
  ...props
}: {
  page: CommunitypostDocument<string>
  adverts: IPaginated<IAdvertCard>
  latestCommunityPosts: {
    page: number
    results_per_page: number
    total_results_size: number
    results_size: number
    total_pages: number
    results: CommunitypostDocument[]
  }
}) => {
  const { t } = useTranslation(['community'])
  const [isCreateCommentModelOpen, setIsCreateCommentModelOpen] =
    useState(false)
  const [replyCommentId, setReplyCommentId] = useState<string | null>(null)
  const router = useRouter()
  const articleId = router.query.id as string | undefined

  const { switchLanguage } = useLanguageSettings()

  useEffect(() => {
    if (props.page) {
      switchLanguage(props.page.lang == 'en-gb' ? 'en' : 'lt')
    }
  }, [props.page, router, switchLanguage])

  const onReplyComment = useCallback((commentId: string) => {
    setIsCreateCommentModelOpen(true)
    setReplyCommentId(commentId)
  }, [])
  const onCloseModal = useCallback(() => {
    setIsCreateCommentModelOpen(false)
    setReplyCommentId(null)
  }, [])

  const { data: comments, isLoading } = useArticleComments({
    payload: {
      articleId: articleId ?? ''
    },
    options: {
      enabled: !!articleId
    }
  })
  if (!props.page) return null
  return (
    <>
      {!!props.page.data.alternativeLanguages.length && (
        <AlternativeArticleLang pageData={props.page} />
      )}

      <ArticleHead communityPost={props.page} />
      <div className="relative mx-auto pt-20 lg:mx-20 lg:flex lg:flex-row-reverse">
        <div className="relative">
          <SocialLinks communityPost={props.page} />
        </div>
        <div className="">
          <SliceZone slices={props.page.data.slices} components={components} />
        </div>
      </div>
      <ArticleComments
        isLoading={isLoading}
        comments={comments}
        className="border-2 border-black bg-white lg:mx-20"
        onCreateComment={() => setIsCreateCommentModelOpen(true)}
        onReplyComment={onReplyComment}
      />

      <Title variant="h3" className="mt-10 px-5 text-3xl xl:px-0">
        {t('trendingJobAds', { ns: 'community' })}
      </Title>
      <AdvertSwiper adverts={adverts.data} />
      <RelatedArticles latestCommunityPosts={props.latestCommunityPosts} />
      <CreateArticleCommentModal
        isOpen={isCreateCommentModelOpen}
        onClose={onCloseModal}
        articleId={articleId}
        parentCommentId={replyCommentId}
      />
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async ({
  params,
  locale: currentLocale,
  previewData,
  defaultLocale
}) => {
  const locale = currentLocale || defaultLocale || 'en'
  const client = createClient({ previewData })
  const postId = params?.id as string

  try {
    const pagePartsPromise = await getFullPageByUID(
      'communitypost',
      postId,
      { lang: '*', locale },
      { previewData }
    )

    const communityPostPromise = client.getByUID('communitypost', postId, {
      lang: '*'
    })

    const i18nPromise = serverSideTranslations(
      locale,
      [
        'actions',
        'inputs',
        'message',
        'community',
        'about_us',
        'crm',
        'zod',
        'ad'
      ],
      null,
      adUtils.getAdvertTranslationsCodes(AdvertTranslation.EnglishLithuanian)
    )

    const latestCommunityPostsPromise = client.getByType('communitypost', {
      lang: getPrismicLangCode(locale),
      pageSize: 3,
      orderings: [
        { field: 'my.communitypost.publishDate', direction: 'desc' },
        { field: 'document.first_publication_date', direction: 'desc' }
      ]
    })

    const advertsPromise = getAdvertsPublic({
      languageCode: locale,
      limit: 20,
      statuses: [AdvertVisibilityType.Active],
      orderingColumn: 'dateCreated',
      order: 'desc'
    })

    const [pageParts, i18n, communityPost, latestCommunityPosts, adverts] =
      await Promise.all([
        pagePartsPromise,
        i18nPromise,
        communityPostPromise,
        latestCommunityPostsPromise,
        advertsPromise
      ])
    const { page, ...layoutParts } = pageParts
    if (!page) {
      return {
        redirect: {
          permanent: false,
          destination: `/${getLocalisedLink('community', locale)}`
        }
      }
    }

    const [seoSlice] = page?.data?.seo ?? []

    const quoteSlice = page.data.slices.find(
      (x) => x.slice_type === 'community_qoute'
    ) as CommunityQouteSlice | undefined

    const metaDescription = asText(quoteSlice?.primary.quote)
    const ogImage = page.data.featuredImage

    const seo: IMetaTagsProps = seoSlice?.primary ?? {
      title: page.data.title + ' | Hrizer',
      metaDescription,
      ogImage,
      twitterCard: 'summary_large_image'
    }

    const markupSchema: IMarkupSchemas = [
      {
        type: 'BreadcrumbList',
        data: {
          items: [
            { name: 'Home', item: baseUrl },
            { name: 'Expert Insights', item: `${baseUrl}/expert-insights` },
            {
              name: page.data.title ?? ''
            }
          ]
        }
      },
      {
        type: 'BlogPosting',
        data: {
          author: [{ name: page.data.AuthorName ?? 'HRizer' }],
          headline: page.data.title ?? '',
          image: [page.data.featuredImage.url ?? ''],
          datePublished: format(
            new Date(page.first_publication_date),
            'yyyy-MM-dd'
          ),
          dateModified: format(
            new Date(page.last_publication_date),
            'yyyy-MM-dd'
          )
        }
      }
    ]
    return {
      props: {
        ...i18n,
        page,
        communityPost,
        latestCommunityPosts: {
          ...latestCommunityPosts,
          results: latestCommunityPosts.results.filter(
            (post) => post.uid !== postId
          )
        },
        seo,
        internal: {
          layout: { ...layoutParts, boundedVariant: BoundedVariant.Small }
        },
        adverts,
        markupSchema
      },
      revalidate: 60 * 60 * 1 //4 hours
    }
  } catch (error) {
    console.error('error', error)
    return {
      redirect: {
        permanent: false,
        destination: `/${getLocalisedLink('community', locale)}`
      }
    }
  }
}

export default CommunityPage
